/**
 * Registry of employer/admin fields per form (invitation send / edit).
 * Fields are optional at send/edit time — admin may fill them later while the employee is working.
 * Keys match PDF AcroForm field names where applicable.
 */

import {
  isEmploymentContractContextComplete,
  isEmploymentContractForm,
} from './step2/FillingDocuments/employmentContract/employmentContractAdminTypes';

export interface InvitationEmployerFieldDef {
  key: string;
  labelJa: string;
  /** Soft hint only; never blocks invitation send/edit. */
  required: boolean;
  multiline?: boolean;
  /** HTML input type; defaults to text. */
  inputType?: 'text' | 'date';
}

/** formCode → field definitions */
export const INVITATION_EMPLOYER_FIELDS: Readonly<Record<string, readonly InvitationEmployerFieldDef[]>> = {
  FF001: [
    { key: 'occupation', labelJa: '職種・職業', required: false },
    { key: 'hire_date', labelJa: '雇用年月日', required: false, inputType: 'date' },
  ],
  FF003: [
    { key: 'workplace_name', labelJa: '作業場名', required: false },
    { key: 'field_agent', labelJa: '現場代理人', required: false },
  ],
  FF004: [
    { key: 'worksite_name', labelJa: '工事現場名', required: false },
    { key: 'site_representative', labelJa: '現場代理人', required: false },
  ],
  FF006: [
    { key: 'payer_name', labelJa: '給与の支払者の氏名又は名称', required: false },
    { key: 'corporate_number', labelJa: '法人番号', required: false },
    { key: 'payer_address', labelJa: '給与の支払者の所在地', required: false, multiline: true },
  ],
  FF007: [
    { key: 'site_manager', labelJa: '所長', required: false },
    { key: 'construction', labelJa: '工事', required: false, multiline: true },
    { key: 'office', labelJa: '事務', required: false, multiline: true },
    { key: 'safety', labelJa: '安全', required: false, multiline: true },
    { key: 'pneu_prevent_measures', labelJa: 'じん肺予防対策責任者', required: false, multiline: true },
    { key: 'person_incharge_company', labelJa: '所属会社責任者職氏名', required: false },
    { key: 'start_date', labelJa: '入場', required: false },
    { key: 'end_date', labelJa: '離場', required: false },
    { key: 'job_type', labelJa: '職種', required: false },
  ],
  /** Custom modal UI — full flat map via employmentContractToFlatMap (open field map). */
  FF002: [
    { key: 'jobDescription', labelJa: '業務内容', required: false, multiline: true },
  ],
};

export type FormEmployerContext = Record<string, Record<string, string>>;

export function normalizeFormCode(formCode: string | undefined | null): string {
  return formCode?.trim().toUpperCase() ?? '';
}

export function formRequiresEmployerFields(formCode: string | undefined | null): boolean {
  const code = normalizeFormCode(formCode);
  if (!code) return false;
  if (isEmploymentContractForm(code)) return true;
  const defs = INVITATION_EMPLOYER_FIELDS[code];
  return Array.isArray(defs) && defs.length > 0;
}

export function getEmployerFieldsForForm(formCode: string | undefined | null): readonly InvitationEmployerFieldDef[] {
  const code = normalizeFormCode(formCode);
  if (!code) return [];
  return INVITATION_EMPLOYER_FIELDS[code] ?? [];
}

export function getEmployerContextForForm(
  context: FormEmployerContext | undefined,
  formCode: string | undefined | null
): Record<string, string> | undefined {
  const code = normalizeFormCode(formCode);
  if (!code || !context) return undefined;
  if (context[code]) return context[code];
  const matchedKey = Object.keys(context).find((key) => normalizeFormCode(key) === code);
  return matchedKey ? context[matchedKey] : undefined;
}

/**
 * True when every defined employer field for the form has a non-empty value.
 * Used for UI completeness hints only (does not block send/edit).
 */
export function isEmployerContextComplete(
  formCode: string | undefined | null,
  fields: Record<string, string> | undefined
): boolean {
  if (isEmploymentContractForm(formCode)) {
    return isEmploymentContractContextComplete(fields);
  }
  const defs = getEmployerFieldsForForm(formCode);
  if (defs.length === 0) return true;
  if (!fields) return false;
  return defs.every((d) => (fields[d.key] ?? '').trim().length > 0);
}

/** Keep employer context only for currently selected form codes (normalized keys). */
export function pruneEmployerContext(
  selectedFormCodes: readonly string[],
  context: FormEmployerContext | undefined
): FormEmployerContext {
  if (!context) return {};
  const selected = new Set(
    selectedFormCodes.map((code) => normalizeFormCode(code)).filter((code) => code.length > 0)
  );
  const pruned: FormEmployerContext = {};
  for (const [formCode, fields] of Object.entries(context)) {
    const normalized = normalizeFormCode(formCode);
    if (selected.has(normalized)) {
      pruned[normalized] = fields;
    }
  }
  return pruned;
}

/** Forms that have employer fields defined but not all filled (advisory only). */
export function findIncompleteEmployerForms(
  selectedFormCodes: readonly string[],
  context: FormEmployerContext | undefined
): string[] {
  const selected = new Set<string>();
  for (const code of selectedFormCodes) {
    const normalized = normalizeFormCode(code);
    if (normalized) selected.add(normalized);
  }
  return [...selected].filter(
    (code) =>
      formRequiresEmployerFields(code) &&
      !isEmployerContextComplete(code, getEmployerContextForForm(context, code))
  );
}

export function formatIncompleteEmployerFormsMessage(incompleteFormCodes: readonly string[]): string {
  if (incompleteFormCodes.length === 0) {
    return '管理者入力は任意です。後から編集で追加できます。';
  }
  return `管理者入力が未入力の記入書類があります（${incompleteFormCodes.join('、')}）。送信・保存は可能です。後から編集で追加できます。`;
}
